<template>
  <v-container style="max-width: 1000px; min-height: 500px; padding-bottom: 100px;">
    <div class="d-flex align-center flex-wrap mt-2">
      <VueCtkDateTimePicker
        :value="range"
        @input="handleRangeChange"
        format="YYYY-MM-DD"
        output-format="x"
        range
        :custom-shortcuts="customShortcuts"
        dialog
        style="max-width: 140px; margin: 0;"
        label="Aikaväli"
        :no-value-to-custom-elem="true"
        auto-close
      >
        <v-btn
          v-tooltip="'Tehdyt palot aikaväliltä'"
          style="width: 100%; z-index: 0;"
          outlined
          small
        >
          <v-icon size="15" class="mr-2">mdi-calendar</v-icon>
          {{ rangeLabel }}
        </v-btn>
      </VueCtkDateTimePicker>

      <ChooseClubSelect
        v-if="currentTeam"
        v-model="leagueClubId"
        class="ml-4 made-outs-control"
        width="260px"
      />

      <v-text-field
        v-model="search"
        class="ml-4 made-outs-control"
        style="max-width: 220px;"
        dense
        hide-details
        clearable
        label="Pelaajahaku"
        @keyup.enter="refresh"
        @click:clear="search = ''; refresh()"
      />

      <v-select
        v-model="limit"
        class="ml-4 made-outs-control"
        style="max-width: 100px;"
        :items="limitOptions"
        dense
        hide-details
        label="Rivejä"
        @change="handleLimitChange"
      />

      <v-btn
        class="ml-4 made-outs-control"
        color="primary"
        small
        outlined
        :loading="loading"
        @click="refresh"
      >
        Päivitä
      </v-btn>
    </div>

    <div class="mt-4 grey--text text--darken-1 text-caption">
      Pelejä: {{ gamesCount }} | Pelaajia: {{ total }} | Näytetään: {{ visibleRange }}
    </div>

    <v-alert v-if="error" class="mt-4" type="error" dense>
      {{ error }}
    </v-alert>

    <Table
      @rowClicked="openPlayerFilter"
      class="mt-5"
      emptyText="Ei tehtyjä paloja"
      :loading="loading"
      :data="tableData"
      subtitle="Avaa pelaajan tekemät palot klikkaamalla riviä"
    />

    <div v-if="totalPages > 1" class="d-flex justify-center mt-5">
      <v-pagination
        v-model="page"
        :length="totalPages"
        :total-visible="7"
        @input="refresh"
      />
    </div>

    <v-card class="mt-8 made-outs-rules" outlined>
      <v-card-title class="text-subtitle-1 font-weight-bold">
        Tilastointi
      </v-card-title>
      <v-card-text>
        <p class="made-outs-pretext grey--text text--darken-3">
          Palon tekijä on se ulkopelaaja, joka toimittaa pallon pesälle polttaen etenijän tai siten että
          etenijä menettää pesäturvan ja siitä seuraa pesäturvan menettäneen etenijän palo. Jos esimerkiksi lukkari tulee
          ottamaan heiton vastaan pois kotipesästä ja tulee takaisin pesään tehden palon, palo merkataan kuitenkin
          suorittaneelle ulkopelaajalle.
        </p>
        <div
          v-for="rule in madeOutRules"
          :key="rule.title"
          class="made-outs-rule"
        >
          <div class="font-weight-bold">
            {{ rule.title }}
          </div>
          <div class="grey--text text--darken-2">
            {{ rule.description }}
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import ax from '@/utils/axios/axios'
import { mapActions, mapGetters } from 'vuex'
import ChooseClubSelect from '@/components/games/ChooseClubSelect.vue'
import Table from '@/components/table/Table.vue'
import { buildFilterUrl } from '@/utils/joukkueet/filterUrl'

export default {
  name: 'MadeOuts',
  components: { ChooseClubSelect, Table },
  data: () => ({
    items: [],
    total: 0,
    gamesCount: 0,
    loading: false,
    error: null,
    range: { shortcut: 'summer' },
    leagueClubId: null,
    search: '',
    limit: 20,
    responseLimit: 20,
    responseOffset: 0,
    page: 1,
    seasonId: null,
    limitOptions: [20, 50, 100, 200],
    madeOutRules: [
      {
        title: 'Viimeisen laiton',
        description: 'Viimeisen laittomasta syntyvää paloa ei merkitä kenellekkään.'
      },
      {
        title: 'Viimeinen lyöjä lyö tyhjään kenttään',
        description: 'Tästä tilanteesta ei merkata kenellekkään paloa.'
      },
      {
        title: 'Kopilla irti',
        description: 'Merkitään kopin ottaneelle pelaajalle.'
      },
      {
        title: 'Kopinnostopalo',
        description: 'Merkitään normaalisti ulkopelaajalle, joka toimittaa pallon pesälle.'
      },
      {
        title: 'Usea palo',
        description: 'Ajotilanteen viimeisellä lyöjä lyö hudin. Lukkari ottaa pallon (tehty palo lukkarille) ja heittää pallon kolmoselle (toinen tehty palo lukkarille). Kolmosvahti ottaa pallon ja heittää ykköselle (tehty palo kolmosvahdille). Eli tästä tapahtumaketjusta kaksi tehtyä paloa lukkarille ja yksi kolmospesällä ollelle.'
      },
      {
        title: 'Estäminen ja muut tekniset palot',
        description: 'Ei kenellekkään.'
      }
    ]
  }),
  created() {
    this.handleRangeChange({ shortcut: 'summer' })
    if(this.leagueId) {
      this.initSeasons(this.leagueId)
        .then(seasons => {
          this.seasonId = seasons.find(season => !season.archived)?.id || seasons[0]?.id || null
        })
    }
  },
  computed: {
    ...mapGetters('user', [
      'currentTeam',
      'currentTeamId',
      'leagueId'
    ]),
    offset() {
      return (this.page - 1) * this.limit
    },
    totalPages() {
      return Math.ceil(this.total / this.limit)
    },
    visibleRange() {
      if(!this.total) return '0'

      const first = this.responseOffset + 1
      const last = Math.min(this.responseOffset + this.items.length, this.total)
      return `${first}-${last}`
    },
    rangeLabel() {
      if(this.range?.shortcut) {
        const shortcut = this.customShortcuts.find(item => item.value === this.range.shortcut || item.key === this.range.shortcut)
        return shortcut?.label || ''
      }

      if(this.range?.start && this.range?.end) {
        return `${this.formatDate(this.range.start)} - ${this.formatDate(this.range.end)}`
      }

      return ''
    },
    customShortcuts() {
      return [
        { key: 'summer', label: 'Kesä', value: 'summer', shortcut: 'summer', isSelected: true },
        { key: 'thisYear', label: 'Tämä vuosi', value: 'year', isSelected: false },
        { key: 'lastYear', label: 'Viime vuosi', value: '-year', isSelected: false },
        { key: 'thisMonth', label: 'Tämä kk', value: 'month', isSelected: false },
        { key: 'lastMonth', label: 'Edellinen kk', value: '-month', isSelected: false },
        { key: 'last30Days', label: 'Viim 30 pv', value: 30, isSelected: false },
        { key: 'last7Days', label: 'Viim. 7 pv', value: 7, isSelected: false }
      ]
    },
    tableData() {
      return {
        headers: [
          { text: ' ', value: 'index', center: true, width: '40px', bold: true, nowrap: true },
          { text: '', value: 'picture_url', width: '60px', type: 'image', locked: true },
          { text: 'Pelaaja', value: 'player_name', center: false, width: '190px', locked: true },
          { text: 'Palot', value: 'made_outs', center: true, width: '90px', bold: true },
          { text: 'Ottelut', value: 'games_played', center: true, width: '90px' },
          { text: 'Joukkue', value: 'club_name_short', center: true, width: '100px' },
        ],
        items: this.items.map((item, index) => {
          return [
            { key: 'index', value: this.offset + index + 1 },
            { key: 'picture_url', value: item.picture_url },
            { key: 'player_name', value: item.player_name || '-' },
            { key: 'made_outs', value: item.made_outs },
            { key: 'games_played', value: item.games_played },
            { key: 'club_name_short', value: item.club_name_short || '-' },
          ]
        })
      }
    }
  },
  watch: {
    leagueClubId() {
      this.page = 1
      this.refresh()
    }
  },
  methods: {
    ...mapActions('admin', [
      'initSeasons'
    ]),
    openPlayerFilter(rowIndex) {
      const player = this.items[rowIndex]
      if(player?.player_id === undefined || player?.player_id === null) return
      if(!this.seasonId || !this.leagueId || !this.currentTeamId) return

      const url = buildFilterUrl({
        season: this.seasonId,
        madeOutPlayers: [player.player_id],
        league_id: this.leagueId,
        team_id: this.currentTeamId
      })

      window.open(url, '_blank', 'noopener,noreferrer')
    },
    handleRangeChange(range) {
      if(range.shortcut === 'summer') {
        const year = new Date().getFullYear()
        range.start = new Date(year, 3, 20).getTime()
        range.end = new Date(year, 8, 30, 23, 59, 59, 999).getTime()
      }

      this.range = range
      this.page = 1
      this.refresh()
    },
    handleLimitChange() {
      this.page = 1
      this.refresh()
    },
    formatDate(timestamp) {
      return new Date(Number(timestamp)).toLocaleDateString('fi-FI')
    },
    refresh() {
      if(!this.range?.start || !this.range?.end || !this.leagueId) return

      const params = {
        start: this.range.start,
        end: this.range.end,
        limit: this.limit,
        offset: this.offset,
        league_id: this.leagueId
      }

      if(this.leagueClubId) params.league_club_id = this.leagueClubId
      if(this.search) params.search = this.search

      this.loading = true
      this.error = null

      ax.get('/event/made-outs', { params })
        .then(res => {
          this.items = res.data.items || []
          this.total = res.data.total || 0
          this.responseLimit = res.data.limit || this.limit
          this.responseOffset = res.data.offset || 0
          this.gamesCount = res.data.games_count || 0
        })
        .catch(err => {
          this.items = []
          this.total = 0
          this.responseLimit = this.limit
          this.responseOffset = 0
          this.gamesCount = 0
          this.error = err.response?.data?.error || 'Tehtyjen palojen haku epäonnistui'
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.made-outs-control {
  margin-bottom: 4px;
}

.made-outs-rules {
  text-align: left;
}

.made-outs-rule + .made-outs-rule {
  margin-top: 16px;
}

.made-outs-pretext {
  line-height: 1.5;
}
</style>
